/**
 * Defines a payment plan for the ingredifind platform.
 */

/**
 * Caps how many products can be imported (e.g. by image upload) within a
 * recurring cycle:
 *   - cycleLengthMonths is the number of months per cycle (0 or 1 => monthly,
 *     3 => quarterly, 6 => six-monthly, 12 => yearly).
 *   - cycleResetMonth (1-12) and cycleResetDay anchor where the boundaries
 *     fall. For a monthly cycle the month is ignored and the quota resets on
 *     the day every month; otherwise boundaries repeat from that month/day in
 *     steps of cycleLengthMonths.
 *   - limit is the number of imports allowed per cycle.
 */
export type ProductImportQuota = {
  cycleLengthMonths: number;
  cycleResetMonth: number;
  cycleResetDay: number;
  limit: number;
};

export type IgfdPaymentPlan = {
  analytics: boolean;
  products: boolean;
  allowManualAllergensAndPreferences: boolean;
  ingredientSearch: boolean;
  dishImages: boolean;
  spiceLevels: boolean;
  dishNutritionDetails: boolean;
  staffLinks: boolean;
  removeIgfdBranding: boolean;
  productImportQuota: ProductImportQuota;
  numLocations: number;
};

export const EMPTY_PRODUCT_IMPORT_QUOTA: ProductImportQuota = {
  cycleLengthMonths: 0,
  cycleResetMonth: 0,
  cycleResetDay: 1,
  limit: 0,
};

/**
 * Resolves the effective product import quota for a plan, defaulting to an
 * empty quota when the plan has none.
 */
export function resolveProductImportQuota(
  plan:
    | {
        productImportQuota?: ProductImportQuota | null;
      }
    | null
    | undefined
): ProductImportQuota {
  return plan?.productImportQuota ?? EMPTY_PRODUCT_IMPORT_QUOTA;
}
